// src/scripts/calendar/adapters/EventDataAdapter.js
/**
 * EventDataAdapter
 *
 * Thin safety wrapper around EventDataManager.
 *
 * Responsibilities:
 * - Owns lifecycle (initialize / refresh / destroy)
 * - Guards access before initialization
 * - Normalizes fallbacks when optional EventDataManager methods are missing
 * - Centralizes error handling and diagnostics
 *
 * Design notes:
 * - This adapter is intentionally defensive and permissive.
 * - It never throws for normal read operations; callers receive safe fallbacks.
 * - Initialization is idempotent and must be completed before use.
 *
 * Usage:
 *   const adapter = new EventDataAdapter(calendarElement);
 *   adapter.initialize();
 *   const events = adapter.getAllEvents();
 *
 * Invariants:
 * - calendarElement must be provided at construction
 * - initialize() must be called before data access
 * - One adapter instance per calendar element
 */


// src/scripts/calendar/adapters/EventDataAdapter.js

import EventDataManager from '../core/EventDataManager.js';
import ErrorHandler from '../../utils/ErrorHandler.js';

export default class EventDataAdapter {
  constructor(calendarElement, logger = null) {
    this.errorHandler = new ErrorHandler({
      module: 'EventDataAdapter',
      maxErrors: 15
    });

    this.calendarElement = calendarElement;
    this.logger = logger || console;

    this.eventManager = null;
    this.initialized = false;
    this._initializing = false;
  }

  /* ------------------------------------------------------------------ */
  /* Initialization                                                     */
  /* ------------------------------------------------------------------ */

  initialize() {
    if (this.initialized) return true;
    if (this._initializing) return false;

    this._initializing = true;

    try {
      this._logInfo('Initializing EventDataAdapter...');

      this._requireCalendarElement();

      this.eventManager = new EventDataManager(this.calendarElement);
      if (!this.eventManager) {
        throw new Error('Failed to create EventDataManager');
      }

      const parsed = this._safeCall(
        'parseEventsFromDOM',
        'dom-parsing-failed',
        false
      );

      if (!parsed) {
        this._logWarn('Failed to parse events from DOM');
        return false;
      }

      this.initialized = true;
      this.errorHandler.logSuccess('EventDataAdapter initialized successfully', {
        category: 'initialization',
        action: 'init-success'
      });

      this._logInfo('✅ EventDataAdapter initialized successfully');
      return true;
    } catch (error) {
      this.errorHandler.handleFatalError(
        'Critical error during EventDataAdapter initialization',
        error,
        { category: 'initialization', action: 'fatal-init-error' }
      );
      this._logError('❌ EventDataAdapter initialization failed:', error);
      return false;
    } finally {
      this._initializing = false;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Public data access                                                  */
  /* ------------------------------------------------------------------ */

  getAllEvents() {
    return this._withInit(() =>
      this._safeCall('getAllEvents', 'get-all-events-failed', [])
    );
  }

  getEventsForFullCalendar() {
    return this._withInit(() =>
      this._safeCall('transformForFullCalendar', 'fullcalendar-transform-failed', [])
    );
  }

  findEventByTitle(title) {
    return this._withInit(() => {
      if (this._hasMethod('findEvents')) {
        const matches = this.eventManager.findEvents(title);
        return matches?.[0] || null;
      }

      return this.getAllEvents().find(e => e.title === title) || null;
    });
  }

  findEvents(criteria) {
    return this._withInit(() => {
      const events = this.getAllEvents();

      if (typeof criteria === 'string') {
        if (this._hasMethod('findEvents')) {
          return this.eventManager.findEvents(criteria);
        }
        return events.filter(e =>
          e.title?.toLowerCase().includes(criteria.toLowerCase())
        );
      }

      if (typeof criteria === 'function') {
        return events.filter(criteria);
      }

      if (criteria && typeof criteria === 'object') {
        return events.filter(event =>
          Object.keys(criteria).every(key => {
            if (key === 'date' && event.start) {
              return (
                new Date(event.start).toDateString() ===
                new Date(criteria[key]).toDateString()
              );
            }
            return event[key] === criteria[key];
          })
        );
      }

      return [];
    });
  }

  getStats() {
    return this._withInit(() => {
      if (this._hasMethod('getEventStats')) {
        return this.eventManager.getEventStats();
      }

      const events = this.getAllEvents();
      return {
        totalEvents: events.length,
        initialized: this.initialized,
        hasEventManager: !!this.eventManager
      };
    });
  }

  refresh() {
    return this._withInit(() => {
      this._logInfo('Refreshing events...');

      const success = this._safeCall(
        'parseEventsFromDOM',
        'refresh-failed',
        false
      );

      success
        ? this._logInfo('✅ Events refreshed successfully')
        : this._logWarn('⚠️ Event refresh failed');

      return success;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Lifecycle & status                                                  */
  /* ------------------------------------------------------------------ */

  isInitialized() {
    return this.initialized && !!this.eventManager;
  }

  getStatus() {
    return {
      initialized: this.initialized,
      hasEventManager: !!this.eventManager,
      hasCalendarElement: !!this.calendarElement,
      eventCount: this.initialized ? this.getAllEvents().length : 0
    };
  }

  getEventManager() {
    return this.eventManager;
  }

  async destroy() {
    try {
      this._logInfo('🗑️ Destroying EventDataAdapter...');

      if (this._hasMethod('destroy')) {
        await this.eventManager.destroy();
      }

      this.eventManager = null;
      this.calendarElement = null;
      this.initialized = false;

      this.errorHandler.logSuccess('EventDataAdapter destroyed successfully', {
        category: 'initialization',
        action: 'destroy-success'
      });

      this._logInfo('✅ EventDataAdapter destroyed');
    } catch (error) {
      this.errorHandler.handleError(
        'Error during EventDataAdapter destruction',
        error,
        { category: 'initialization', action: 'destroy-failed' }
      );
      this._logError('❌ Error destroying EventDataAdapter:', error);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Error + diagnostics                                                 */
  /* ------------------------------------------------------------------ */

  getErrors() {
    return this._hasMethod('getErrors') ? this.eventManager.getErrors() : [];
  }

  getRecentErrors(minutes = 5) {
    return this.errorHandler.getRecentErrors(minutes);
  }

  hasCriticalIssues() {
    return this.errorHandler.hasCriticalIssues();
  }

  clearErrorHistory() {
    this.errorHandler.clearErrorHistory();
  }

  getAdapterStats() {
    const errorStats = this.errorHandler.getErrorStats();
    return {
      ...this.getStatus(),
      errors: {
        total: errorStats.totalErrors,
        fatal: errorStats.fatalErrors,
        recent: errorStats.recentErrors,
        byCategory: errorStats.errorsByCategory
      }
    };
  }

  /* ------------------------------------------------------------------ */
  /* Internal helpers                                                    */
  /* ------------------------------------------------------------------ */

  _withInit(fn) {
    try {
      this._ensureInitialized();
      return fn();
    } catch (error) {
      this.errorHandler.handleError('Adapter operation failed', error, {
        category: 'fallback',
        action: 'operation-failed'
      });
      return Array.isArray(fn()) ? [] : null;
    }
  }

  _safeCall(method, action, fallback) {
    if (!this._hasMethod(method)) {
      this.errorHandler.handleError(
        `EventManager.${method} not available`,
        null,
        { category: 'configuration', action }
      );
      return fallback;
    }

    try {
      return this.eventManager[method]();
    } catch (error) {
      this.errorHandler.handleError(
        `Error executing ${method}`,
        error,
        { category: 'fallback', action }
      );
      return fallback;
    }
  }

  _hasMethod(name) {
    return !!(this.eventManager && typeof this.eventManager[name] === 'function');
  }

  _ensureInitialized() {
    if (!this.initialized || !this.eventManager) {
      this.errorHandler.handleFatalError(
        'EventDataAdapter not initialized',
        null,
        {
          category: 'configuration',
          action: 'adapter-not-initialized',
          initialized: this.initialized,
          hasEventManager: !!this.eventManager
        }
      );
      throw new Error('EventDataAdapter not initialized. Call initialize() first.');
    }
  }

  _requireCalendarElement() {
    if (!this.calendarElement) {
      this.errorHandler.handleFatalError(
        'Calendar element is required for initialization',
        null,
        { category: 'initialization', action: 'missing-calendar-element' }
      );
      throw new Error('Calendar element is required');
    }
  }

  _logInfo(...args) {
    this.logger?.info && this.logger.info(...args);
  }
  _logWarn(...args) {
    this.logger?.warn && this.logger.warn(...args);
  }
  _logError(...args) {
    this.logger?.error && this.logger.error(...args);
  }
}
